// src/components/Course/CourseForm.tsx
import React, { useState } from 'react';
import { TextField, Button, Container, Box, Paper } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AuthoringAssistantPanel from '../AI/AuthoringAssistantPanel';
import { AuthoringAssistantGeneratedDraftResponse } from '../../services/authoringAssistantService';

interface CourseFormData {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  subject: string;
  subjectSpecification: string;
}

const CourseForm: React.FC<{ courseId?: number; initialData?: { name: string; description: string; startDate: string; endDate: string; subject: string; subjectSpecification: string; }, onSubmit: (data: { name: string; description: string; startDate: string; endDate: string; subject: string; subjectSpecification: string; }) => void; assistantInitialPrompt?: string; assistantAutoGenerate?: boolean }> = ({ courseId, initialData, onSubmit, assistantInitialPrompt, assistantAutoGenerate = false }) => {
  const [formData, setFormData] = useState(initialData || { name: '', description: '', startDate: '', endDate: '', subject: '', subjectSpecification: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleApplyDraft = (draft: AuthoringAssistantGeneratedDraftResponse) => {
    if (!draft.course) {
      return;
    }

    setFormData((previousData: CourseFormData) => ({
      ...previousData,
      name: draft.course?.name || previousData.name,
      description: draft.course?.description || previousData.description,
      subject: draft.course?.subject || previousData.subject,
      subjectSpecification:
        draft.course?.subjectSpecification || previousData.subjectSpecification,
      startDate: draft.course?.startDate || previousData.startDate,
      endDate: draft.course?.endDate || previousData.endDate,
    }));
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexDirection: { xs: 'column', xl: 'row' } }}>
        <Paper elevation={1} sx={{ p: 3, flex: 1, width: '100%' }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nimi"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Kuvaus"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              multiline
              rows={4}
              fullWidth
            />
            <TextField
              label="Aloituspäivä"
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Lopetuspäivä"
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Aine"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Aineen tarkenne"
              name="subjectSpecification"
              value={formData.subjectSpecification}
              onChange={handleChange}
              required
              fullWidth
            />
            <Button variant="contained" color="primary" type="submit" startIcon={<SaveIcon />}>
              Tallenna
            </Button>
          </Box>
        </Paper>

        <AuthoringAssistantPanel
          courseId={courseId}
          draftCourse={formData}
          draftTarget="course"
          onApplyDraft={handleApplyDraft}
          initialPrompt={assistantInitialPrompt}
          autoGenerateInitialDraft={assistantAutoGenerate}
        />
      </Box>
    </Container>
  );
};

export default CourseForm;
